/**
 * Turn-by-turn engine. Converts a stream of GPS fixes into navigation progress:
 * which step we're on, distance to the next maneuver that actually needs a
 * decision, which lanes to be in, and whether we've left the route.
 *
 * The important behaviour here is MANEUVER MERGING. Mapbox emits a step every
 * time its road model changes, so a highway run produces a chain of
 * "keep right to stay on 304" events. Announcing each one is noise and, worse,
 * it aimed the distance countdown at a non-event. We fold those runs together
 * (see significance.ts) and count down to the next real decision instead.
 *
 * Announcements are composed here rather than replayed from Mapbox, so that
 * every word is in the driver's chosen language (see phrasing.ts) and the lane
 * warning is spoken in the same breath as the turn.
 *
 * Deliberately has no DOM or map dependencies so its behaviour can be reasoned
 * about (and driven from a replayed track) in isolation.
 */

import type { BannerInstruction, Fix, Lane, NavProgress, Route, RouteStep } from '../core/types';
import type { UnitSystem } from '../core/settings';
import type { Lang } from '../core/i18n';
import { RouteIndex } from './routeIndex';
import { uturnSide } from './turnSide';
import { laneAdvice, laneVisibilityRange, resolveLanes, type LaneAdvice } from './lanes';
import { nextFork, FORK_SPEAK_AT_M, type ForkInfo } from './forks';
import { nextDivergence, type Divergence } from './divergence';
import { buildAnnouncement, forkSentence, laneSentence, laneMoveSentence } from './phrasing';

/** Deviation beyond this, sustained, means we're off route. */
const OFF_ROUTE_METRES = 45;
/** Consecutive off-route fixes before we commit to rerouting. Rejects blips. */
const OFF_ROUTE_FIXES = 4;
/** Within this distance of the final waypoint we call it arrived. */
const ARRIVAL_METRES = 30;

export interface NavigatorConfig {
  units: UnitSystem;
  lang: Lang;
  /**
   * Optional AI guidance for a junction, by step index. A PURE lookup: the
   * navigator does no networking and never waits on anything, so whoever owns
   * the cache does the fetching and this simply reads whatever has arrived.
   * That is what keeps this class drivable from a replayed track. See
   * ai/laneAdvisor.ts.
   */
  guideFor?: (stepIndex: number) => { guide: string; side: string | null } | null;
}

export interface NavigatorEvents {
  onProgress?: (p: NavProgress) => void;
  onAnnounce?: (text: string) => void;
  onOffRoute?: () => void;
  onArrive?: () => void;
  onStepChange?: (p: NavProgress) => void;
}

/**
 * Announcement tiers.
 *
 * Two, at fixed distances, on the driver's instruction: "the voice should be
 * 1.5km before, and 300m before thats it." The previous ladder had four rungs
 * scaled by speed, which meant four announcements per junction in town - the
 * app talked more than it helped.
 */
type Tier = 'far' | 'near';

/** Metres before the maneuver at which each announcement is made. */
const FAR_AT_M = 1500;
const NEAR_AT_M = 300;

/**
 * Metres before a non-maneuver split at which its single warning is spoken.
 * Further out than the fork cue: the driver may be several lanes from the one
 * that stays on route, and at 100 km/h moving three lanes takes room.
 */
const DIVERGENCE_SPEAK_AT_M = 700;

export class RouteNavigator {
  private index: RouteIndex;
  private lastVertex = 0;
  private currentStep = 0;
  private offRouteStreak = 0;
  private spoken = new Set<string>();
  /** Last sentence spoken, so two tiers that render alike don't repeat it. */
  private lastSpoken = '';
  private arrived = false;
  /** Distance along route at the previous fix, used to reject backwards jitter. */
  private lastAlong = 0;

  constructor(
    route: Route,
    private events: NavigatorEvents = {},
    private config: () => NavigatorConfig = () => ({ units: 'metric', lang: 'en' }),
  ) {
    this.index = new RouteIndex(route);
  }

  get route(): Route {
    return this.index.route;
  }

  /** Swap in a fresh route after a reroute, preserving nothing. */
  replaceRoute(route: Route): void {
    this.index = new RouteIndex(route);
    this.lastVertex = 0;
    this.currentStep = 0;
    this.offRouteStreak = 0;
    this.lastAlong = 0;
    this.arrived = false;
    this.spoken.clear();
    this.lastSpoken = '';
  }

  update(fix: Fix): NavProgress {
    const snapped = this.index.snap([fix.lng, fix.lat], this.lastVertex);
    this.lastVertex = snapped.segmentIndex;

    // GPS jitter can nudge us backwards; ignore small regressions so the
    // distance-to-turn readout never counts up while we're driving at it.
    let along = snapped.distanceAlongRoute;
    if (along < this.lastAlong && this.lastAlong - along < 25) {
      along = this.lastAlong;
    }
    this.lastAlong = along;

    const stepIdx = this.index.stepAt(along, this.currentStep);
    const stepChanged = stepIdx !== this.currentStep;
    this.currentStep = stepIdx;

    const current = this.index.steps[stepIdx];

    // The maneuver we're actually driving towards, skipping the lane-keeping
    // nudges that share our road number.
    const targetIdx = this.index.nextSignificant(stepIdx);
    const target = this.index.steps[targetIdx] ?? null;
    // The banner and lane data for a maneuver live on the step BEFORE it.
    const approach = this.index.steps[Math.max(0, targetIdx - 1)] ?? current;

    const maneuverAt = target ? target.startDistance : this.index.totalDistance;
    const distanceToManeuver = Math.max(0, maneuverAt - along);
    const distanceRemaining = Math.max(0, this.index.totalDistance - along);

    // Scale remaining time by how much of the route is left. Cruder than
    // re-requesting an ETA, but free and stable; the traffic-aware total from
    // the API is still the basis.
    const fraction = this.index.totalDistance > 0 ? distanceRemaining / this.index.totalDistance : 0;
    // Prefer the pace-adjusted total (the driver's own cruising speeds plus
    // red-light time) so the live ETA matches the one they chose the route on.
    const totalDuration = this.index.route._paceDuration ?? this.index.route.duration;
    const durationRemaining = totalDuration * fraction;

    // Off-route: require a run of bad fixes, and don't trip on a wide accuracy
    // circle (a 60 m accuracy fix is not evidence of leaving the road). While
    // stationary, GPS drift produces exactly this pattern - a parked car must
    // never trigger the reroute loop, so demand actual movement unless the
    // deviation is far beyond drift range.
    const tolerance = OFF_ROUTE_METRES + Math.min(40, Math.max(0, fix.accuracy - 10));
    const trustworthy = fix.accuracy <= 60;
    const moving = fix.speed == null || fix.speed > 1.5;
    if (snapped.deviation > tolerance && trustworthy && (moving || snapped.deviation > 150)) {
      this.offRouteStreak++;
    } else if (snapped.deviation <= tolerance) {
      this.offRouteStreak = 0;
    }
    const offRoute = this.offRouteStreak >= OFF_ROUTE_FIXES;

    const banner = pickBanner(approach.step.bannerInstructions, distanceToManeuver);
    const drivingSide = approach.step.driving_side ?? 'left';

    const speed = fix.speed ?? 0;
    const laneRange = laneVisibilityRange(speed);
    // Lanes come from the TARGET step - the maneuver's own intersection - while
    // the banner comes from the step we are driving along. Passing `approach`
    // here read the last lane-bearing junction we had already gone through, and
    // that mismatch produced every wrong lane bar we have chased. See lanes.ts.
    //
    // Resolved at EVERY distance, not only inside the display range. The range
    // governs when the bar is drawn, and gating the model on it too made the
    // 1.5 km trap warning below unreachable: the range caps at 1400 m and the
    // far tier fires at 1500, so `advice` was always null when it was consulted
    // and the trap could never be spoken early - at any speed. Display stays
    // gated; the announcement now sees the data it was written to use.
    const rawLanes = resolveLanes(banner, target?.step ?? null);
    // The maneuver's own modifier is passed in so laneAdvice can reject lane
    // data that contradicts the turn (see nav/lanes.ts).
    // U-turn side is measured from the geometry rather than assumed from the
    // driving side - they are on either side here depending on the junction.
    const modifier = target?.step.maneuver.modifier;
    const measuredUturn =
      (modifier ?? '').toLowerCase() === 'uturn'
        ? uturnSide(current?.step ?? null, target?.step ?? null, drivingSide)
        : null;
    const advice = laneAdvice(rawLanes, drivingSide, modifier, measuredUturn);

    const followIdx = target ? this.index.nextSignificant(targetIdx) : -1;
    const follow = followIdx > targetIdx ? (this.index.steps[followIdx] ?? null) : null;

    // The branch in front of the windscreen, which is rarely the maneuver the
    // card is counting down to. Shown constantly, spoken once - see forks.ts.
    const fork = nextFork(this.index.steps, stepIdx, targetIdx, along, speed, drivingSide);

    // The lane bar is drawn only inside the display range; the advice above is
    // computed regardless so the voice can use it earlier.
    const lanesInRange = distanceToManeuver <= laneRange;

    /*
     * A split that is not a maneuver - the case the driver hit in the third
     * lane of four. Mapbox emits no step for it, so nothing else here would
     * ever see it. See nav/divergence.ts.
     *
     * Treated as "the route continues straight through", because it does: the
     * modifier passed to laneAdvice is `straight`, which runs the same
     * cross-check that rejects a lane model whose usable lanes all turn away.
     */
    const div = nextDivergence(this.index.divergences, along, speed);
    const divAdvice = div ? laneAdvice(div.lanes, drivingSide, 'straight', null) : null;
    const divergence =
      div && divAdvice
        ? { distance: Math.max(0, div.along - along), lanes: div.lanes, advice: divAdvice }
        : null;

    const progress: NavProgress = {
      legIndex: current.legIndex,
      stepIndex: current.stepIndex,
      distanceToManeuver,
      distanceRemaining,
      durationRemaining,
      step: current.step,
      maneuverStep: target?.step ?? null,
      nextStep: follow?.step ?? null,
      banner,
      /*
       * A null advice with lanes present means the cross-checks in laneAdvice
       * rejected the lane model as describing some other junction or maneuver.
       * The bar must then stay blank too, not just the spoken advice: at the
       * Khao Lam Rd bypass split the rejected model (a U-turn lane second from
       * the left, straight lanes lit either side of it) was still being
       * painted, and a wrong lane bar misleads exactly like a wrong sentence.
       */
      lanes: lanesInRange && advice ? rawLanes : null,
      laneAdvice: lanesInRange ? advice : null,
      fork,
      divergence,
      /*
       * AI guidance for whichever junction is the live decision - the fork if
       * one is in range, otherwise the maneuver being counted down to.
       *
       * ADDITIVE ONLY. This is read by the card alongside the rule-based
       * advice, never in place of it: the rules are the part that has been
       * measured against real routes, and a model that agrees adds nothing
       * while a model that disagrees must not win. Its value is the junctions
       * where the rules produce nothing at all.
       */
      aiGuide: this.config().guideFor?.(fork ? fork.stepIndex : targetIdx) ?? null,
      drivingSide,
      snapped,
      offRoute,
    };

    if (target) {
      this.speakIfDue(target.step, targetIdx, distanceToManeuver, advice, follow?.step ?? null);
    }
    this.speakForkIfDue(fork);
    if (div && divAdvice) this.speakDivergenceIfDue(div, divAdvice, along);

    if (stepChanged) this.events.onStepChange?.(progress);
    this.events.onProgress?.(progress);

    if (offRoute) {
      this.offRouteStreak = 0; // let the caller decide; don't spam
      this.events.onOffRoute?.();
    }

    if (!this.arrived && distanceRemaining <= ARRIVAL_METRES) {
      this.arrived = true;
      this.events.onArrive?.();
    }

    return progress;
  }

  /**
   * Announcement ladder. Distances scale with speed so the highway warning
   * arrives with time to change three lanes, while a town turn isn't
   * announced from half a kilometre away.
   */
  private speakIfDue(
    maneuverStep: RouteStep,
    targetIdx: number,
    distance: number,
    advice: LaneAdvice | null,
    followStep: RouteStep | null,
  ): void {
    const tiers: Array<{ tier: Tier; at: number }> = [
      { tier: 'far', at: FAR_AT_M },
      { tier: 'near', at: NEAR_AT_M },
    ];

    // Pick the most specific tier we've entered but not yet spoken.
    let due: Tier | null = null;
    for (const { tier, at } of tiers) {
      if (distance <= at && !this.spoken.has(`${targetIdx}:${tier}`)) due = tier;
    }
    if (!due) return;

    // Entering a tier implies every coarser one is moot - mark them spoken so
    // resuming mid-approach doesn't replay a backlog of "in 1 kilometre...".
    for (const { tier } of tiers) {
      this.spoken.add(`${targetIdx}:${tier}`);
      if (tier === due) break;
    }

    // Don't stack the 1.5 km warning on top of the previous maneuver: if the
    // two are closer together than that, it would fire while the driver is
    // still completing the last turn.
    const runLength = this.runLengthTo(targetIdx);
    if (due === 'far' && runLength < FAR_AT_M) return;

    const { units, lang } = this.config();
    const trap = advice?.trap != null;
    const text = buildAnnouncement({
      maneuverStep,
      distance,
      units,
      lang,
      advice,
      // Lanes at 300 m, where they can still be acted on - and at 1.5 km too
      // when the lane is a trap, because three lanes is a long way to move.
      withLanes: due === 'near' || trap,
      // Only preview the follow-on when it lands right after this one.
      thenStep: due === 'near' ? this.closeFollow(targetIdx, followStep) : null,
    });

    // Guard against two tiers rendering the same sentence.
    if (text === this.lastSpoken) return;
    this.lastSpoken = text;

    this.events.onAnnounce?.(text);
  }

  /**
   * The single spoken cue for a fork, at FORK_SPEAK_AT_M.
   *
   * One utterance per fork, ever - no ladder. The whole reason forks were
   * filtered out of the announcement path was that they talked too much, so the
   * cue has to stay cheap enough that restoring it does not undo that. It is
   * also suppressed while a real maneuver announcement is close, so the driver
   * never gets "keep left" stepping on "in 300 metres, turn right".
   */
  private speakForkIfDue(fork: ForkInfo | null): void {
    if (!fork || fork.distance > FORK_SPEAK_AT_M) return;
    const key = `fork:${fork.stepIndex}`;
    if (this.spoken.has(key)) return;
    this.spoken.add(key);

    const { lang } = this.config();
    // The lane advice rides along in the same breath when the fork actually
    // constrains which lane holds the branch - that is the whole point of
    // resolving lanes at forks, and a separate utterance would arrive too late.
    const lane = fork.advice?.changeRequired ? laneSentence(fork.advice, lang) : '';
    const text = [forkSentence(fork, lang), lane].filter(Boolean).join('. ');
    if (!text || text === this.lastSpoken) return;
    this.lastSpoken = text;
    this.events.onAnnounce?.(text);
  }

  /**
   * The one spoken warning for a non-maneuver split.
   *
   * Spoken because the whole failure was silence: the arrows on every lane look
   * identical, so unlike a turn there is nothing out of the window to prompt
   * the driver, and a purely visual cue can be missed entirely. Once per split,
   * keyed by its signature so a run of identical junctions down a ramp approach
   * cannot repeat it.
   *
   * The positive instruction leads - "use the right lane" tells you where to
   * GO, which is what someone sitting in the wrong lane needs. The trap clause
   * follows only when the lane they are most likely already in is a losing one.
   */
  private speakDivergenceIfDue(div: Divergence, advice: LaneAdvice, along: number): void {
    if (!advice.changeRequired) return;
    if (div.along - along > DIVERGENCE_SPEAK_AT_M) return;
    if (this.spoken.has(div.key)) return;
    this.spoken.add(div.key);

    const { lang } = this.config();
    const move = laneMoveSentence(advice, lang);
    const trap = advice.trap ? laneSentence(advice, lang) : '';
    const text = [move, trap].filter(Boolean).join('. ');
    if (!text || text === this.lastSpoken) return;
    this.lastSpoken = text;
    this.events.onAnnounce?.(text);
  }

  /** Distance from the previous significant maneuver to this one. */
  private runLengthTo(targetIdx: number): number {
    const target = this.index.steps[targetIdx];
    if (!target) return 0;
    const prev = this.index.previousSignificant(targetIdx);
    const from = prev >= 0 ? this.index.steps[prev].startDistance : 0;
    return Math.max(0, target.startDistance - from);
  }

  /** The follow-on maneuver, but only if it arrives soon enough to matter. */
  private closeFollow(targetIdx: number, followStep: RouteStep | null): RouteStep | null {
    if (!followStep) return null;
    const followIdx = this.index.nextSignificant(targetIdx);
    const a = this.index.steps[targetIdx];
    const b = this.index.steps[followIdx];
    if (!a || !b) return null;
    return b.startDistance - a.startDistance <= 500 ? followStep : null;
  }
}

/**
 * Choose the banner in force. Mapbox gives several per step, each with the
 * distance at which it should *start* showing; the correct one is the most
 * specific (smallest trigger distance) that we're already inside.
 */
export function pickBanner(
  banners: BannerInstruction[] | undefined,
  distanceToManeuver: number,
): BannerInstruction | null {
  if (!banners?.length) return null;
  let best: BannerInstruction | null = null;
  for (const b of banners) {
    if (distanceToManeuver <= b.distanceAlongGeometry) {
      if (!best || b.distanceAlongGeometry < best.distanceAlongGeometry) best = b;
    }
  }
  return best ?? banners.reduce((a, b) => (a.distanceAlongGeometry > b.distanceAlongGeometry ? a : b));
}

/** Re-exported for the UI, which renders whatever the navigator resolved. */
export type { Lane };
export { OFF_ROUTE_METRES, ARRIVAL_METRES };
